import React, { useRef } from 'react';
import { ImageIcon, Upload, X } from 'lucide-react';
import { useUserImages, useUploadImage } from '../../../services/userImageService';

const BrandImagePicker = ({
  label,
  value,
  onChange,
  hint,
  aspectClass = 'aspect-video',
  uploadImageType = 'general',
  showGallery = true,
  galleryParams = { non_portfolio: true },
}) => {
  const fileInputRef = useRef(null);
  const { data: imagesResponse, isLoading } = useUserImages(
    showGallery ? galleryParams : {},
    { staleTime: 60_000, enabled: showGallery }
  );
  const uploadMutation = useUploadImage();

  const images = showGallery
    ? (imagesResponse?.data || imagesResponse?.images || []).filter(
        (img) => img.image_type !== 'portfolio'
      )
    : [];

  const handleUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);
    formData.append('image_type', uploadImageType);

    uploadMutation.mutate(
      { formData },
      {
        onSuccess: (res) => {
          const url = res?.data?.image_url || res?.image_url;
          if (url) onChange(url);
        },
      }
    );
    event.target.value = '';
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <label className="text-sm font-medium text-gray-900">{label}</label>
        {value && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-xs text-gray-500 hover:text-red-600 inline-flex items-center gap-1"
          >
            <X className="w-3 h-3" />
            Remove
          </button>
        )}
      </div>

      {value ? (
        <div className={`relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50 ${aspectClass}`}>
          <img src={value} alt={label} className="w-full h-full object-cover" />
        </div>
      ) : (
        <div className={`flex items-center justify-center rounded-lg border-2 border-dashed border-gray-200 bg-gray-50 ${aspectClass}`}>
          <div className="text-center px-4 py-6">
            <ImageIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No image selected</p>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadMutation.isPending}
          className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          <Upload className="w-4 h-4" />
          {uploadMutation.isPending ? 'Uploading…' : 'Upload new'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={handleUpload}
        />
      </div>

      {hint && <p className="text-xs text-gray-500">{hint}</p>}

      {showGallery && images.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-600 mb-2">Choose from your gallery</p>
          <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-36 overflow-y-auto pr-1">
            {isLoading ? (
              [...Array(4)].map((_, i) => (
                <div key={i} className="aspect-square rounded-md bg-gray-100 animate-pulse" />
              ))
            ) : (
              images.map((img) => {
                const url = img.image_url;
                if (!url) return null;
                const selected = value === url;
                return (
                  <button
                    key={img.id}
                    type="button"
                    onClick={() => onChange(url)}
                    className={`relative aspect-square rounded-md overflow-hidden border-2 transition-all ${
                      selected ? 'border-black ring-2 ring-black/20' : 'border-transparent hover:border-gray-300'
                    }`}
                  >
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandImagePicker;
